import React from "react";
import { Box, Chip, List, ListItemButton, ListItemIcon, ListItemText, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import RuleFolderOutlinedIcon from "@mui/icons-material/RuleFolderOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import AutoModeOutlinedIcon from "@mui/icons-material/AutoModeOutlined";
import SchemaOutlinedIcon from "@mui/icons-material/SchemaOutlined";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import { NavLink } from "react-router-dom";
import { platformRoutes } from "../config/platformRoutes";
import { usePlatform } from "../platform/store";

const iconMap = {
  "/": <DashboardCustomizeOutlinedIcon sx={{ fontSize: 16 }} />,
  "/compliance-checker": <ScienceOutlinedIcon sx={{ fontSize: 16 }} />,
  "/perfume-dashboard": <RuleFolderOutlinedIcon sx={{ fontSize: 16 }} />,
  "/labeling": <LabelOutlinedIcon sx={{ fontSize: 16 }} />,
  "/document-submission": <Inventory2OutlinedIcon sx={{ fontSize: 16 }} />,
  "/document-repository": <DescriptionOutlinedIcon sx={{ fontSize: 16 }} />,
  "/vendor-management": <ApartmentOutlinedIcon sx={{ fontSize: 16 }} />,
  "/compliance-tracker": <TaskAltOutlinedIcon sx={{ fontSize: 16 }} />,
  "/compliance-automation": <AutoModeOutlinedIcon sx={{ fontSize: 16 }} />,
  "/workflow-automation": <SchemaOutlinedIcon sx={{ fontSize: 16 }} />,
  "/regulatory-intelligence": <TravelExploreOutlinedIcon sx={{ fontSize: 16 }} />,
};

const sectionOrder = ["Project", "Operations", "Watch"];

const Sidebar = ({ onNavigate }) => {
  const { dashboard } = usePlatform();
  const theme = useTheme();

  return (
    // Sidebar — Liquid Glass CHROME tier.
    // Outer radius 18; inner padding 12 → nav items use radius 12 (concentric).
    <Box
      component="nav"
      sx={{
        width: 220,
        flexShrink: 0,
        flexGrow: 0,
        alignSelf: "stretch",
        height: "100%",
        minHeight: 0,
        py: 1.5,
        px: 1.5,
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        position: "relative",
        // Chrome tier — edge-attached, so the surface reads as a soft
        // translucent wall rather than a floating tile. Apple's chrome
        // blur is less opaque than surface blur and uses ONLY a hairline
        // separator + subtle top rim shadow. No specular ::before pseudo
        // here — that's reserved for floating surface-tier cards inside
        // the sidebar (the Release box still has its own specular).
        background: "rgba(255,255,255,0.42)",
        backdropFilter: "blur(30px) saturate(180%) brightness(1.04)",
        WebkitBackdropFilter: "blur(30px) saturate(180%) brightness(1.04)",
        borderRight: "1px solid rgba(0,0,0,0.06)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.40)",
        "@media print": {
          background: "#FFFFFF !important",
          backdropFilter: "none !important",
          WebkitBackdropFilter: "none !important",
          boxShadow: "none !important",
        },
      }}
    >
      {sectionOrder.map((section) => {
        const items = platformRoutes.filter((r) => r.section === section);
        return (
          <Box key={section}>
            {/* Section eyebrow — theme overline carries the right scale/letterspacing */}
            <Typography
              variant="overline"
              sx={{ px: 1, pb: 0.5, display: "block" }}
            >
              {section}
            </Typography>
            <List disablePadding sx={{ display: "grid", gap: 0.5 }}>
              {items.map((item) => {
                const enabled = item.enabled !== false;
                return (
                  <ListItemButton
                    key={item.path}
                    component={enabled ? NavLink : "div"}
                    to={enabled ? item.path : undefined}
                    end={item.path === "/app"}
                    onClick={enabled ? onNavigate : undefined}
                    disabled={!enabled}
                    sx={{
                      // Generous padding + 36px min-height so the sidebar reads airy.
                      py: 0.75,
                      px: 1.25,
                      minHeight: 36,
                      borderRadius: "12px", // concentric inside an 18-radius chrome
                      opacity: enabled ? 1 : 0.35,
                      color: "rgba(0,0,0,0.78)",
                      backgroundColor: "transparent",
                      transition: `background ${theme.motion.tint}, color ${theme.motion.tint}`,
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.45)",
                      },
                      "&:active": { transform: "scale(0.98)" },
                      "&.active": {
                        // Brand-accent navy tint at ~16% layered over INSET glass.
                        background: `linear-gradient(rgba(27,42,74,0.16), rgba(27,42,74,0.16)), ${theme.glass.inset.background}`,
                        backdropFilter: theme.glass.inset.backdropFilter,
                        WebkitBackdropFilter: theme.glass.inset.WebkitBackdropFilter,
                        border: theme.glass.inset.border,
                        boxShadow: theme.glass.inset.boxShadow,
                        // Navy[700] = brand mark on the active row.
                        color: "#1B2A4A",
                        transition: `background ${theme.motion.tint}, color ${theme.motion.tint}, box-shadow ${theme.motion.morph}`,
                        "& .MuiListItemIcon-root": { color: "#1B2A4A" },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 28, color: "rgba(0,0,0,0.60)", transition: `color ${theme.motion.tint}` }}>
                      {iconMap[item.path]}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="subtitle2" fontWeight={500} sx={{ color: "inherit" }}>{item.label}</Typography>
                          {!enabled && <Chip label="Soon" size="small" sx={{ height: 18 }} />}
                        </Stack>
                      }
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        );
      })}

      {/* Release/version box — SURFACE tier glass, gold accent stays for text. */}
      <Box
        sx={{
          mt: "auto",
          // Generous internal padding so the release strapline breathes.
          p: 2,
          borderRadius: "12px", // concentric to nav-item radius
          position: "relative",
          background: theme.glass.surface.background,
          backdropFilter: theme.glass.surface.backdropFilter,
          WebkitBackdropFilter: theme.glass.surface.WebkitBackdropFilter,
          border: theme.glass.surface.border,
          boxShadow: theme.glass.surface.boxShadow,
          transition: `transform ${theme.motion.morph}, box-shadow ${theme.motion.morph}`,
          // Specular highlight along the top-left edge.
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            background: theme.glass.specular,
            opacity: 0.6,
            pointerEvents: "none",
            mixBlendMode: "soft-light",
          },
          "& > *": { position: "relative" },
        }}
      >
        {/* Gold[700] / Warning brand-mark colours — preserved as the release accent. */}
        <Typography variant="overline" sx={{ color: "#7A631D", display: "block" }}>Release</Typography>
        <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "#92400E" }}>
          {dashboard.readyProducts} ready &middot; {dashboard.blockingIssues} blocking
        </Typography>
      </Box>
    </Box>
  );
};

export default Sidebar;
